<template>
  <div class="flex flex-col gap-4">
    <div v-if="loading" class="text-sm text-[#7CADB9]">
      {{ $t('requestHistory.loading') }}
    </div>

    <div v-else-if="error" class="text-sm text-red-500">
      {{ error }}
    </div>

    <template v-else>
      <div class="rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] overflow-x-auto">
        <table class="min-w-full text-sm text-left">
          <thead class="bg-[var(--c-primary)] text-white">
            <tr>
              <th class="px-4 py-3 font-semibold whitespace-nowrap">{{ $t('requestHistory.columns.date') }}</th>
              <th class="px-4 py-3 font-semibold whitespace-nowrap">{{ $t('requestHistory.columns.user') }}</th>
              <th class="px-4 py-3 font-semibold whitespace-nowrap">{{ $t('requestHistory.columns.action') }}</th>
              <th class="px-4 py-3 font-semibold">{{ $t('requestHistory.columns.before') }}</th>
              <th class="px-4 py-3 font-semibold">{{ $t('requestHistory.columns.after') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!entries.length">
              <td colspan="5" class="px-4 py-6 text-center text-[#7CADB9]">
                {{ $t('requestHistory.empty') }}
              </td>
            </tr>
            <tr v-for="entry in entries" :key="entry.id" class="border-t border-gray-200 align-top">
              <td class="px-4 py-3 whitespace-nowrap text-[#385660]">{{ formatDate(entry.logged_at) }}</td>
              <td class="px-4 py-3 text-[#385660]">{{ userName(entry.user) }}</td>
              <td class="px-4 py-3 whitespace-nowrap text-[#385660] font-semibold">{{ actionLabel(entry.action) }}</td>
              <td class="px-4 py-3 text-[#7CADB9]">
                <ul v-if="hasValues(entry.old_values)" class="flex flex-col gap-1">
                  <li v-for="(value, field) in entry.old_values" :key="`old-${field}`">
                    <span class="font-semibold">{{ fieldLabel(field) }}:</span> {{ formatValue(value, entry, field) }}
                  </li>
                </ul>
                <span v-else>—</span>
              </td>
              <td class="px-4 py-3 text-[#7CADB9]">
                <ul v-if="hasValues(entry.new_values)" class="flex flex-col gap-1">
                  <li v-for="(value, field) in entry.new_values" :key="`new-${field}`">
                    <span class="font-semibold">{{ fieldLabel(field) }}:</span> {{ formatValue(value, entry, field) }}
                  </li>
                </ul>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalPages > 1" class="flex items-center justify-center gap-4">
        <button
          class="px-4 py-2 rounded-md border border-[#2AB4D9] text-[#2AB4D9] disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="page <= 1"
          @click="load(page - 1)"
        >
          {{ $t('requestHistory.previous') }}
        </button>
        <span class="text-sm text-[#7CADB9]">{{ page }} / {{ totalPages }}</span>
        <button
          class="px-4 py-2 rounded-md border border-[#2AB4D9] text-[#2AB4D9] disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="page >= totalPages"
          @click="load(page + 1)"
        >
          {{ $t('requestHistory.next') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { fetchRequestHistory } from '@/services/requestHistoryAccess';
import { createRequestHistoryFormatter } from '@/services/requestHistoryFormatter';

const props = defineProps({
  requestId: {
    type: [String, Number],
    required: true,
  },
});

const { t, te, locale } = useI18n();

const entries = ref([]);
const page = ref(1);
const totalPages = ref(1);
const loading = ref(false);
const error = ref('');

const load = async (targetPage = 1) => {
  loading.value = true;
  error.value = '';
  try {
    const result = await fetchRequestHistory(props.requestId, { page: targetPage });
    entries.value = result?.data ?? [];
    page.value = result?.current_page ?? targetPage;
    totalPages.value = result?.last_page ?? 1;
  } catch (e) {
    error.value = e.response?.data?.value || t('requestHistory.error');
    entries.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(() => load(1));
watch(() => props.requestId, () => load(1));

const formatDate = (value, dateOnly = false) => {
  if (!value) return '—';

  const localeName = locale.value === 'ca' ? 'ca-ES' : 'es-ES';
  if (dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [year, month, day] = String(value).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(localeName);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(localeName, dateOnly
      ? { dateStyle: 'short' }
      : { dateStyle: 'short', timeStyle: 'short' });
};

const userName = (user) => {
  if (!user) return t('requestHistory.unknownUser');
  return [user.name, user.surname].filter(Boolean).join(' ') || user.email || t('requestHistory.unknownUser');
};

const actionLabel = (action) => {
  const key = `requestHistory.actions.${action}`;
  return te(key) ? t(key) : action;
};

const hasValues = (values) => Boolean(values) && Object.keys(values).length > 0;

const { fieldLabel, formatValue } = createRequestHistoryFormatter({ t, te, formatDate });
</script>
